import { useState, useEffect } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../config/firebase";
import adminCalendarService from "../../services/adminCalendarService";

const PLATFORMS = [
  {
    id: "viator",
    label: "Viator",
    color: "bg-emerald-100 text-emerald-700 border-emerald-300",
  },
  {
    id: "tripadvisor",
    label: "TripAdvisor",
    color: "bg-teal-100 text-teal-700 border-teal-300",
  },
  {
    id: "getyourguide",
    label: "GetYourGuide",
    color: "bg-orange-100 text-orange-700 border-orange-300",
  },
  {
    id: "direct",
    label: "Direct",
    color: "bg-sky-100 text-sky-700 border-sky-300",
  },
  {
    id: "other",
    label: "Other",
    color: "bg-gray-100 text-gray-700 border-gray-300",
  },
];
const PLATFORM_MAP = Object.fromEntries(PLATFORMS.map((p) => [p.id, p]));

function getServiceTimeSlotOptions(serviceId) {
  if (serviceId === "jet-ski") {
    return [
      { value: "09:00 - 10:00", label: "Slot 1 · 09:00 – 10:00" },
      { value: "11:00 - 12:00", label: "Slot 2 · 11:00 – 12:00" },
      { value: "16:00 - 17:00", label: "Slot 3 · 16:00 – 17:00" },
      { value: "18:00 - 19:00", label: "Slot 4 · 18:00 – 19:00" },
    ];
  }
  if (serviceId === "luxury-yacht") {
    return [
      { value: "10:00 - 14:00", label: "Morning Charter · 10:00 – 14:00" },
      { value: "16:00 - 20:00", label: "Sunset Charter · 16:00 – 20:00" },
    ];
  }
  if (serviceId === "secret-beach-escape") {
    return [
      { value: "09:00 - 13:00", label: "Morning · 09:00 – 13:00" },
      { value: "14:00 - 18:00", label: "Afternoon · 14:00 – 18:00" },
    ];
  }
  if (serviceId === "bayliner") {
    return [
      { value: "09:00 - 09:30", label: "30-min · 09:00 – 09:30" },
      { value: "09:00 - 10:00", label: "60-min · 09:00 – 10:00" },
      { value: "10:00 - 10:30", label: "30-min · 10:00 – 10:30" },
      { value: "11:00 - 11:30", label: "30-min · 11:00 – 11:30" },
      { value: "11:00 - 12:00", label: "60-min · 11:00 – 12:00" },
      { value: "14:00 - 14:30", label: "30-min · 14:00 – 14:30" },
      { value: "14:00 - 15:00", label: "60-min · 14:00 – 15:00" },
      { value: "15:00 - 15:30", label: "30-min · 15:00 – 15:30" },
      { value: "16:00 - 16:30", label: "30-min · 16:00 – 16:30" },
      { value: "16:00 - 17:00", label: "60-min · 16:00 – 17:00" },
      { value: "17:00 - 17:30", label: "30-min · 17:00 – 17:30" },
    ];
  }
  // speedboat — Boat Cruise (90 min) × 5 slots + Extended (120 min) × 2 slots
  return [
    { value: "09:00 - 10:30", label: "Morning Tour · 09:00 – 10:30" },
    { value: "09:00 - 11:00", label: "Extended · 09:00 – 11:00" },
    { value: "12:00 - 13:30", label: "Midday Tour · 12:00 – 13:30" },
    { value: "15:00 - 16:30", label: "Afternoon Tour · 15:00 – 16:30" },
    { value: "17:00 - 18:30", label: "Evening Tour · 17:00 – 18:30" },
    { value: "18:00 - 20:00", label: "Extended · 18:00 – 20:00" },
    { value: "19:00 - 20:30", label: "Night Tour · 19:00 – 20:30" },
  ];
}

const SERVICES = [
  { id: "speedboat", label: "SpeedBoat Tour" },
  { id: "bayliner", label: "Bayliner Water Sports" },
  { id: "jet-ski", label: "Jet Ski Rental" },
  { id: "luxury-yacht", label: "Luxury Yacht" },
  { id: "secret-beach-escape", label: "Secret Beach Escape" },
];

const EXTRAS_DRINKS = [
  { id: "water", name: "Bottled Water", price: 2 },
  { id: "softdrink", name: "Soft Drink", price: 3 },
  { id: "beer", name: "Local Beer", price: 5 },
  { id: "champagne", name: "Champagne", price: 30 },
];
const EXTRAS_FOOD = [
  { id: "seafood-salad", name: "Cold seafood salad", price: 20 },
  { id: "mix-crudo", name: "Mix crudo", price: 25 },
  { id: "linguine-seafood", name: "Linguine seafood", price: 15 },
  { id: "risotto-seafood", name: "Risotto seafood", price: 15 },
  { id: "seafood-fried", name: "Seafood mix fried", price: 20 },
  { id: "seafood-grilled", name: "Seafood mix grilled", price: 25 },
  { id: "chocolate-souffle", name: "Chocolate soufflé", price: 5 },
];

// Returns the monetary value of the extras object (drinks + food + jetSki)
function computeExtrasTotal(extras) {
  if (!extras) return 0;
  const drinks = EXTRAS_DRINKS.reduce(
    (s, item) => s + (extras.drinks?.[item.id] || 0) * item.price,
    0,
  );
  const food = EXTRAS_FOOD.reduce(
    (s, item) => s + (extras.food?.[item.id] || 0) * item.price,
    0,
  );
  return drinks + food + (extras.jetSki ? 60 : 0);
}

const STATUSES = ["all", "confirmed", "completed", "cancelled"];
const STATUS_COLORS = {
  confirmed: "bg-blue-100 text-blue-800 border-blue-300",
  completed: "bg-green-100 text-green-800 border-green-300",
  cancelled: "bg-red-100 text-red-800 border-red-300",
};

const EMPTY_FORM = {
  platform: "viator",
  guestName: "",
  guestEmail: "",
  guestPhone: "",
  guestCount: 2,
  date: "",
  timeSlot: "",
  serviceId: "speedboat",
  notes: "",
  price: "",
  status: "confirmed",
  referenceId: "",
  extras: { drinks: {}, food: {}, jetSki: false },
};

function PlatformBadge({ platform }) {
  const p = PLATFORM_MAP[platform] || PLATFORM_MAP.other;
  return (
    <span
      className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${p.color}`}
    >
      {p.label}
    </span>
  );
}

function StatusBadge({ status }) {
  return (
    <span
      className={`text-xs font-semibold px-2 py-0.5 rounded-full border capitalize ${STATUS_COLORS[status] || STATUS_COLORS.confirmed}`}
    >
      {status}
    </span>
  );
}

export default function ExternalBookingsPanel({
  onDataChange,
  onNavigateToDate,
}) {
  const [bookings, setBookings] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [msg, setMsg] = useState(null);

  const [platformFilter, setPlatformFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const PER_PAGE = 20;

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isCustomSlot, setIsCustomSlot] = useState(false);
  const [showExtras, setShowExtras] = useState(false);

  const notify = (text, type = "info") => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3500);
  };

  // Real-time listener — uses snapshot documents DIRECTLY so price/extras
  // changes from the Add Extras page are reflected instantly without a
  // secondary getDocs call that could serve stale cached data.
  useEffect(() => {
    setLoading(true);
    const unsub = onSnapshot(
      collection(db, "externalBookings"),
      (snapshot) => {
        const docs = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort(
            (a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0),
          );
        setBookings(docs);
        setLoading(false);
      },
      () => {
        setMsg({ text: "Error loading external bookings", type: "error" });
        setLoading(false);
      },
    );
    return () => unsub();
  }, []); // stable: collection reference never changes

  // Manual refresh — forces a getDocs server read as a fallback
  const handleRefresh = async () => {
    try {
      const data = await adminCalendarService.getExternalBookings();
      setBookings(data);
    } catch {
      notify("Error refreshing", "error");
    }
  };

  useEffect(() => {
    let out = [...bookings];
    if (platformFilter !== "all")
      out = out.filter((b) => b.platform === platformFilter);
    if (statusFilter !== "all")
      out = out.filter((b) => b.status === statusFilter);
    if (search.trim()) {
      const s = search.toLowerCase();
      out = out.filter(
        (b) =>
          b.guestName?.toLowerCase().includes(s) ||
          b.referenceId?.toLowerCase().includes(s) ||
          b.date?.includes(s) ||
          b.serviceId?.toLowerCase().includes(s),
      );
    }
    setFiltered(out);
    setPage(0);
  }, [bookings, platformFilter, statusFilter, search]);

  const openAdd = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setShowExtras(false);
    setIsCustomSlot(false);
    setShowForm(true);
  };

  const openEdit = (b) => {
    setEditId(b.id);
    // Store the BASE price (total minus any already-computed extras value) so
    // that when the admin saves without changes the stored price stays identical,
    // and when they add/remove extras the final price = basePrice + extrasTotal.
    const existingExtras = b.extras || {
      drinks: {},
      food: {},
      jetSki: false,
    };
    const existingExtrasTotal = computeExtrasTotal(existingExtras);
    const basePrice = Math.max(0, (b.price || 0) - existingExtrasTotal);
    setForm({
      platform: b.platform,
      guestName: b.guestName || "",
      guestEmail: b.guestEmail || "",
      guestPhone: b.guestPhone || "",
      guestCount: b.guestCount,
      date: b.date,
      timeSlot: b.timeSlot || "",
      serviceId: b.serviceId,
      notes: b.notes || "",
      price: basePrice,
      status: b.status,
      referenceId: b.referenceId || "",
      extras: existingExtras,
    });
    setShowExtras(false);
    // auto-detect custom slot when editing
    const presets = getServiceTimeSlotOptions(b.serviceId);
    setIsCustomSlot(
      !!b.timeSlot && !presets.find((o) => o.value === b.timeSlot),
    );
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (
      !form.guestName.trim() ||
      !form.date ||
      !form.serviceId ||
      !form.platform
    ) {
      notify("Please fill all required fields", "error");
      return;
    }
    setSaving(true);
    try {
      // Always persist price = basePrice + extrasTotal so revenue stats stay accurate
      const extrasTotal = computeExtrasTotal(form.extras);
      const finalPrice = Number(form.price || 0) + extrasTotal;
      const formToSave = { ...form, price: finalPrice };
      if (editId) {
        const prev = bookings.find((b) => b.id === editId);
        await adminCalendarService.updateExternalBooking(
          editId,
          formToSave,
          prev,
        );
        notify("Booking updated", "success");
      } else {
        await adminCalendarService.addExternalBooking(formToSave);
        notify("External booking added — calendar updated", "success");
        if (onNavigateToDate && form.date) onNavigateToDate(form.date);
      }
      setShowForm(false);
      setIsCustomSlot(false);
      if (onDataChange) await onDataChange();
    } catch (err) {
      notify("Error: " + err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Delete this external booking? Capacity will be released on the calendar.",
      )
    )
      return;
    setDeleting(id);
    try {
      await adminCalendarService.deleteExternalBooking(id);
      notify("Deleted — calendar updated", "success");
      if (onDataChange) onDataChange();
    } catch (err) {
      notify("Error: " + err.message, "error");
    } finally {
      setDeleting(null);
    }
  };

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const pageItems = filtered.slice(page * PER_PAGE, (page + 1) * PER_PAGE);

  return (
    <div className="space-y-4">
      {/* Notification */}
      {msg && (
        <div
          className={`px-4 py-2 rounded-lg text-sm font-medium border
          ${
            msg.type === "success"
              ? "bg-green-50 text-green-800 border-green-300"
              : msg.type === "error"
                ? "bg-red-50 text-red-800 border-red-300"
                : "bg-blue-50 text-blue-800 border-blue-300"
          }`}
        >
          {msg.text}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-bold text-gray-800 text-lg">
            External Platform Bookings
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Bookings from Viator, TripAdvisor, GetYourGuide and other platforms
            — automatically synced with the admin &amp; user calendar.
          </p>
        </div>
        <button
          onClick={openAdd}
          className="px-4 py-2 bg-sky-600 text-white text-sm font-semibold rounded-xl hover:bg-sky-700 transition flex items-center gap-1.5 flex-shrink-0"
        >
          + Add External Booking
        </button>
      </div>

      {/* Filters */}
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            placeholder="Search name, reference, date…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border rounded-xl px-4 py-2 text-sm w-full sm:flex-1 sm:max-w-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
          />
          <button
            onClick={handleRefresh}
            className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm hover:bg-gray-200 transition"
          >
            ↻ Refresh
          </button>
        </div>

        {/* Platform pills */}
        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => setPlatformFilter("all")}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition
              ${platformFilter === "all" ? "bg-sky-600 text-white border-sky-600" : "bg-white text-gray-600 border-gray-300 hover:border-sky-400"}`}
          >
            All Platforms
          </button>
          {PLATFORMS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPlatformFilter(p.id)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition
                ${platformFilter === p.id ? "bg-sky-600 text-white border-sky-600" : "bg-white text-gray-600 border-gray-300 hover:border-sky-400"}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Status pills */}
        <div className="flex flex-wrap gap-1.5">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium border capitalize transition
                ${statusFilter === s ? "bg-slate-700 text-white border-slate-700" : "bg-white text-gray-600 border-gray-300 hover:border-slate-400"}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <p className="text-xs text-gray-400">
        {filtered.length} external booking{filtered.length !== 1 ? "s" : ""}
      </p>

      {/* List */}
      {loading ? (
        <div className="h-40 flex items-center justify-center text-gray-400">
          Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="h-40 flex items-center justify-center text-gray-300 text-lg">
          No external bookings found.
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="sm:hidden space-y-3">
            {pageItems.map((b) => (
              <div
                key={b.id}
                className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm space-y-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-1">
                    <p className="font-semibold text-gray-800">{b.guestName}</p>
                    {(b.guestEmail || b.guestPhone) && (
                      <p className="text-xs text-gray-400">
                        {b.guestEmail}
                        {b.guestEmail && b.guestPhone ? " · " : ""}
                        {b.guestPhone}
                      </p>
                    )}
                    <p className="text-xs text-gray-400">
                      {b.date}
                      {b.timeSlot ? ` · ${b.timeSlot}` : ""}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <PlatformBadge platform={b.platform} />
                    <StatusBadge status={b.status} />
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="capitalize">
                    {SERVICES.find((s) => s.id === b.serviceId)?.label ||
                      b.serviceId}
                  </span>
                  <span>
                    {b.guestCount} guest{b.guestCount !== 1 ? "s" : ""}
                  </span>
                  <span className="font-bold text-gray-800">
                    €{b.price || 0}
                  </span>
                </div>
                {b.referenceId && (
                  <p className="text-xs text-gray-400 font-mono">
                    Ref: {b.referenceId}
                  </p>
                )}
                {/* Extras added via Add Extras page */}
                {(() => {
                  const drinks = Object.entries(b.extras?.drinks || {}).filter(
                    ([, q]) => q > 0,
                  );
                  const food = Object.entries(b.extras?.food || {}).filter(
                    ([, q]) => q > 0,
                  );
                  const hasExtras =
                    drinks.length || food.length || b.extras?.jetSki;
                  if (!hasExtras) return null;
                  const allItems = [
                    ...drinks.map(
                      ([id, qty]) =>
                        `${EXTRAS_DRINKS.find((x) => x.id === id)?.name || id} ×${qty}`,
                    ),
                    ...food.map(
                      ([id, qty]) =>
                        `${EXTRAS_FOOD.find((x) => x.id === id)?.name || id} ×${qty}`,
                    ),
                    ...(b.extras?.jetSki ? ["Jet Ski on Board"] : []),
                  ];
                  return (
                    <p className="text-xs text-sky-600">
                      + Extras: {allItems.join(", ")}
                    </p>
                  );
                })()}
                {b.notes && (
                  <p className="text-xs text-gray-500 italic">{b.notes}</p>
                )}
                <div className="flex gap-2 pt-2 border-t">
                  <button
                    onClick={() => openEdit(b)}
                    className="flex-1 py-1.5 bg-sky-50 text-sky-700 border border-sky-200 rounded-xl text-xs font-semibold hover:bg-sky-100"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(b.id)}
                    disabled={deleting === b.id}
                    className="flex-1 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-xl text-xs font-semibold hover:bg-red-100 disabled:opacity-50"
                  >
                    {deleting === b.id ? "…" : "Delete"}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {[
                    "Platform",
                    "Guest",
                    "Date / Time",
                    "Service",
                    "Guests",
                    "Price",
                    "Status",
                    "Reference",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pageItems.map((b) => (
                  <tr key={b.id} className="hover:bg-gray-50 group">
                    <td className="px-4 py-3">
                      <PlatformBadge platform={b.platform} />
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{b.guestName}</p>
                      {b.guestEmail && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {b.guestEmail}
                        </p>
                      )}
                      {b.guestPhone && (
                        <p className="text-xs text-gray-400">{b.guestPhone}</p>
                      )}
                      {b.notes && (
                        <p className="text-xs text-gray-400 italic mt-0.5 max-w-[160px] truncate">
                          {b.notes}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {b.date}
                      {b.timeSlot && (
                        <>
                          <br />
                          <span className="text-xs">{b.timeSlot}</span>
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 capitalize">
                      {SERVICES.find((s) => s.id === b.serviceId)?.label ||
                        b.serviceId}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-700">
                      {b.guestCount}
                    </td>
                    <td className="px-4 py-3 font-bold text-gray-800">
                      €{b.price || 0}
                      {(() => {
                        const drinks = Object.entries(
                          b.extras?.drinks || {},
                        ).filter(([, q]) => q > 0);
                        const food = Object.entries(
                          b.extras?.food || {},
                        ).filter(([, q]) => q > 0);
                        const hasExtras =
                          drinks.length || food.length || b.extras?.jetSki;
                        if (!hasExtras) return null;
                        const allItems = [
                          ...drinks.map(
                            ([id, qty]) =>
                              `${EXTRAS_DRINKS.find((x) => x.id === id)?.name || id} ×${qty}`,
                          ),
                          ...food.map(
                            ([id, qty]) =>
                              `${EXTRAS_FOOD.find((x) => x.id === id)?.name || id} ×${qty}`,
                          ),
                          ...(b.extras?.jetSki ? ["Jet Ski"] : []),
                        ];
                        return (
                          <div className="text-xs font-normal text-sky-600 mt-0.5">
                            {allItems.join(", ")}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={b.status} />
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-400">
                      {b.referenceId || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => openEdit(b)}
                          className="px-2.5 py-1 bg-sky-50 text-sky-700 border border-sky-200 rounded-lg text-xs font-medium hover:bg-sky-100"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(b.id)}
                          disabled={deleting === b.id}
                          className="px-2.5 py-1 bg-red-50 text-red-700 border border-red-200 rounded-lg text-xs font-medium hover:bg-red-100 disabled:opacity-50"
                        >
                          {deleting === b.id ? "…" : "Del"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i)}
                  className={`w-7 h-7 rounded-full text-xs font-medium transition
                    ${page === i ? "bg-sky-600 text-white" : "bg-white text-gray-600 border border-gray-300 hover:border-sky-400"}`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Add / Edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-bold text-gray-800">
                {editId ? "Edit External Booking" : "Add External Booking"}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setIsCustomSlot(false);
                }}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Platform */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Platform *
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {PLATFORMS.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, platform: p.id }))}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition
                        ${form.platform === p.id ? "bg-sky-600 text-white border-sky-600" : "bg-white text-gray-600 border-gray-300 hover:border-sky-400"}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Guest name + reference */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Guest Name *
                  </label>
                  <input
                    type="text"
                    value={form.guestName}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, guestName: e.target.value }))
                    }
                    className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                    placeholder="e.g. John Smith"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Platform Reference
                  </label>
                  <input
                    type="text"
                    value={form.referenceId}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, referenceId: e.target.value }))
                    }
                    className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                    placeholder="e.g. HMWF123"
                  />
                </div>
              </div>

              {/* Guest email + phone */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Guest Email
                  </label>
                  <input
                    type="email"
                    value={form.guestEmail}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, guestEmail: e.target.value }))
                    }
                    className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                    placeholder="e.g. john@email.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Guest Phone
                  </label>
                  <input
                    type="tel"
                    value={form.guestPhone}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, guestPhone: e.target.value }))
                    }
                    className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                    placeholder="e.g. +355 69 123 4567"
                  />
                </div>
              </div>

              {/* Date + time slot */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, date: e.target.value }))
                    }
                    className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Activity Slot
                  </label>
                  <select
                    value={isCustomSlot ? "__custom__" : form.timeSlot}
                    onChange={(e) => {
                      if (e.target.value === "__custom__") {
                        setIsCustomSlot(true);
                        setForm((f) => ({ ...f, timeSlot: "" }));
                      } else {
                        setIsCustomSlot(false);
                        setForm((f) => ({ ...f, timeSlot: e.target.value }));
                      }
                    }}
                    className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                  >
                    <option value="">— No specific slot —</option>
                    {getServiceTimeSlotOptions(form.serviceId).map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                    <option value="__custom__">✏️ Custom time...</option>
                  </select>
                  {isCustomSlot && (
                    <input
                      type="text"
                      placeholder="e.g. 13:00 - 15:00"
                      value={form.timeSlot}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, timeSlot: e.target.value }))
                      }
                      className="mt-1.5 w-full border border-sky-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-sky-50"
                      autoFocus
                    />
                  )}
                </div>
              </div>

              {/* Service + guest count */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Service *
                  </label>
                  <select
                    value={form.serviceId}
                    onChange={(e) => {
                      setIsCustomSlot(false);
                      setForm((f) => ({
                        ...f,
                        serviceId: e.target.value,
                        timeSlot: "",
                      }));
                    }}
                    className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                  >
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Guest Count *
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={form.guestCount}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, guestCount: e.target.value }))
                    }
                    className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                  />
                </div>
              </div>

              {/* Price + status */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Base Price (€)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={form.price}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, price: e.target.value }))
                    }
                    className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                    placeholder="0"
                  />
                  {(() => {
                    const extTotal = computeExtrasTotal(form.extras);
                    if (extTotal === 0) return null;
                    const total = Number(form.price || 0) + extTotal;
                    return (
                      <p className="text-xs text-sky-600 mt-1">
                        + Extras €{extTotal} →{" "}
                        <span className="font-bold">Total €{total}</span>
                      </p>
                    );
                  })()}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Status
                  </label>
                  <select
                    value={form.status}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, status: e.target.value }))
                    }
                    className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                  >
                    <option value="confirmed">Confirmed</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Notes
                </label>
                <textarea
                  value={form.notes}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, notes: e.target.value }))
                  }
                  className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 resize-none"
                  rows={2}
                  placeholder="Any additional notes…"
                />
              </div>

              {/* Extras / Add-ons */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowExtras((v) => !v)}
                  className="w-full flex items-center justify-between text-xs font-semibold text-gray-600 uppercase tracking-wider px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition"
                >
                  <span>Extras / Add-ons</span>
                  <span className="text-gray-400 text-base leading-none">
                    {showExtras ? "▲" : "▼"}
                  </span>
                </button>

                {showExtras && (
                  <div className="mt-2 border border-gray-200 rounded-xl p-4 space-y-4">
                    {/* Drinks */}
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        Drinks
                      </p>
                      <div className="space-y-2">
                        {EXTRAS_DRINKS.map((item) => {
                          const qty = form.extras?.drinks?.[item.id] || 0;
                          return (
                            <div
                              key={item.id}
                              className="flex items-center justify-between"
                            >
                              <span className="text-sm text-gray-700">
                                {item.name}{" "}
                                <span className="text-gray-400 text-xs">
                                  €{item.price}
                                </span>
                              </span>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setForm((f) => ({
                                      ...f,
                                      extras: {
                                        ...f.extras,
                                        drinks: {
                                          ...f.extras.drinks,
                                          [item.id]: Math.max(
                                            0,
                                            (f.extras.drinks?.[item.id] || 0) -
                                              1,
                                          ),
                                        },
                                      },
                                    }))
                                  }
                                  className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 text-sm leading-none transition"
                                >
                                  −
                                </button>
                                <span className="text-sm font-medium w-5 text-center">
                                  {qty}
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setForm((f) => ({
                                      ...f,
                                      extras: {
                                        ...f.extras,
                                        drinks: {
                                          ...f.extras.drinks,
                                          [item.id]:
                                            (f.extras.drinks?.[item.id] || 0) +
                                            1,
                                        },
                                      },
                                    }))
                                  }
                                  className="w-6 h-6 rounded-full bg-sky-100 text-sky-600 hover:bg-sky-200 text-sm leading-none transition"
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Food */}
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                        Food
                      </p>
                      <p className="text-[10px] text-gray-400 mb-2">
                        🚤 Another boat brings food to guests at sea
                      </p>
                      <div className="space-y-2">
                        {EXTRAS_FOOD.map((item) => {
                          const qty = form.extras?.food?.[item.id] || 0;
                          return (
                            <div
                              key={item.id}
                              className="flex items-center justify-between"
                            >
                              <span className="text-sm text-gray-700">
                                {item.name}{" "}
                                <span className="text-gray-400 text-xs">
                                  €{item.price}
                                </span>
                              </span>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setForm((f) => ({
                                      ...f,
                                      extras: {
                                        ...f.extras,
                                        food: {
                                          ...f.extras.food,
                                          [item.id]: Math.max(
                                            0,
                                            (f.extras.food?.[item.id] || 0) - 1,
                                          ),
                                        },
                                      },
                                    }))
                                  }
                                  className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 text-sm leading-none transition"
                                >
                                  −
                                </button>
                                <span className="text-sm font-medium w-5 text-center">
                                  {qty}
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setForm((f) => ({
                                      ...f,
                                      extras: {
                                        ...f.extras,
                                        food: {
                                          ...f.extras.food,
                                          [item.id]:
                                            (f.extras.food?.[item.id] || 0) + 1,
                                        },
                                      },
                                    }))
                                  }
                                  className="w-6 h-6 rounded-full bg-sky-100 text-sky-600 hover:bg-sky-200 text-sm leading-none transition"
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Jet Ski Add-on */}
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-gray-700">
                          Jet Ski Add-on
                        </span>
                        <span className="text-xs text-gray-400 ml-1.5">
                          15 min on-board · €60
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            extras: {
                              ...f.extras,
                              jetSki: !f.extras.jetSki,
                            },
                          }))
                        }
                        className={`relative w-10 h-5 rounded-full transition-colors ${
                          form.extras.jetSki ? "bg-sky-500" : "bg-gray-200"
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                            form.extras.jetSki
                              ? "translate-x-5"
                              : "translate-x-0.5"
                          }`}
                        />
                      </button>
                    </div>

                    {/* Extras total */}
                    {(() => {
                      const dTotal = EXTRAS_DRINKS.reduce(
                        (s, item) =>
                          s +
                          (form.extras?.drinks?.[item.id] || 0) * item.price,
                        0,
                      );
                      const snTotal = EXTRAS_FOOD.reduce(
                        (s, item) =>
                          s + (form.extras?.food?.[item.id] || 0) * item.price,
                        0,
                      );
                      const total =
                        dTotal + snTotal + (form.extras?.jetSki ? 60 : 0);
                      if (total === 0) return null;
                      return (
                        <div className="pt-2 border-t border-gray-100 flex justify-between text-sm font-semibold text-gray-700">
                          <span>Extras Total</span>
                          <span>€{total}</span>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Info note */}
              <p className="text-xs text-gray-400 bg-sky-50 rounded-lg px-3 py-2 border border-sky-100">
                💡 Adding this booking will update the calendar availability for
                the selected date, so it correctly reflects occupied capacity.
              </p>

              {/* Buttons */}
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => setShowForm(false)}
                  className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="flex-1 py-2.5 bg-sky-600 text-white rounded-xl text-sm font-semibold hover:bg-sky-700 disabled:opacity-50 transition"
                >
                  {saving
                    ? "Saving…"
                    : editId
                      ? "Update Booking"
                      : "Add Booking"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
